/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
`use strict`
import { Row, Col, Container } from 'react-bootstrap';
import { getProbes, Probes, ProbeFilter } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { useState, useEffect, useContext } from 'react';
import ProbeTable from './ProbeTable';
import { Link } from 'react-router-dom';
import { Stack } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { useSearchParams } from "react-router-dom";
import { Button } from 'react-bootstrap';
import PurgeProbeModal from './PurgeProbeModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';

const defaultProbe: Probes = {
    probes: [],
    run_ids: [],
    probe_types: [],
};

function ProbePage({ }) {
    const [ data, setData ] = useState<Probes>(defaultProbe);
    const [ rowSelection, setRowSelection ] = useState<object>([]);
    const [ editId, setEditId ] = useState<number>(0);
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ showPurgeModal, setShowPurgeModal ] = useState<boolean>(false);

    const f_pass = searchParams.get('pass');
    const f_run_id = searchParams.get('run_id');
    const f_probe_type = searchParams.get('probe_type');

    const statusContext = useContext(StatusContext) as StatusContextType;

    useEffect(() => {
        const fetchData = async () => {
            try {
                let filter: ProbeFilter = {};
                const f_pass = searchParams.get('pass') || 'all';
                const f_run_id = searchParams.get('run_id') || 'all';
                const f_probe_type = searchParams.get('probe_type') || 'all';
                const response = await getProbes({
                    pass: f_pass === 'all' ? undefined : f_pass,
                    run_id: f_run_id === 'all' ? undefined : f_run_id,
                    probe_type: f_probe_type === 'all' ? undefined : f_probe_type,
                } as ProbeFilter);
                setData(response);
            } catch (error) {
                console.error('Error fetching probe data:', error);
                statusContext.postError(`Error fetching probe data: ${error.message}`);
                setData(defaultProbe);
            }
        }
        fetchData();
    }, [ searchParams ]);

    return (
        <>
        <Container fluid>
            <Row>
                <Col xs={4} className="ps-4">
                    <h1>Probes</h1>
                </Col>
                <Col xs={1}>
                    <Button variant="danger"  onClick={() => setShowPurgeModal(true)}>
                        <FontAwesomeIcon icon={faTrashCan} />
                    </Button>
                </Col>
                <Col xs={2} >
                    <Form.Group controlId="probePassFilter">
                        <Form.Label>Passed</Form.Label>
                        <Form.Select
                            value={f_pass}
                            onChange={(e) => {
                                setSearchParams((prev) => {
                                    prev.set('pass', e.target.value);
                                    return prev;
                                });
                            }}
                            aria-label="Filter by pass status"
                        >
                            <option value="all">All</option>
                            <option value="pass">Pass</option>
                            <option value="fail">Fail</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={2} >
                    <Form.Group controlId="probeRunIdFilter">
                        <Form.Label>Run ID</Form.Label>
                        <Form.Select
                            value={f_run_id}
                            aria-label="Filter by run id"
                            onChange={(e) => {
                                setSearchParams((prev) => {
                                    prev.set('run_id', e.target.value);
                                    return prev;
                                });
                            }}
                        >
                            <option value="all">All Runs</option>
                            {data.run_ids.map((run_id: any) => (
                                <option key={run_id} value={run_id}>
                                    {run_id}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={2} className="pe-3">
                    <Form.Group controlId="probeTypeFilter">
                        <Form.Label>Probe Type</Form.Label>
                        <Form.Select
                            className="form-select"
                            value={f_probe_type}
                            onChange={(e) => {
                                setSearchParams((prev) => {
                                    prev.set('probe_type', e.target.value);
                                    return prev;
                                });
                            }}
                            aria-label="Filter by probe type"
                        >
                            <option value="all">All</option>
                            {data.probe_types.map((probe_type: any) => (
                                <option key={probe_type} value={probe_type}>
                                    {probe_type}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col xs={5} className="ps-4"></Col>
                <Col xs={6} className="ps-4 mt-2">
                    <Stack direction="horizontal" gap={3}>
                        <Badge pill bg="primary">Cnt: {data.probes.length}</Badge>
                        {f_pass === 'pass' && (
                            <Badge pill bg="success">
                                Pass ONLY
                            </Badge>
                        )}
                        {f_pass === 'fail' && (
                            <Badge pill bg="warning">
                                Fail ONLY
                            </Badge>
                        )}
                        {f_run_id !== 'all' && f_run_id && (
                            <Badge pill bg="primary">
                                Run {f_run_id}
                            </Badge>
                        )}
                        {f_probe_type !== 'all' && (
                            <Badge pill bg="primary">
                                {f_probe_type}
                            </Badge>
                        )}
                    </Stack>
                </Col>
            </Row>
            <Row>
                <Col xs={12} >
                    <ProbeTable
                        data={data.probes}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        doSearch={true}
                    />
                </Col>
            </Row>
        </Container>
        { showPurgeModal &&
            <PurgeProbeModal show={showPurgeModal} setShow={setShowPurgeModal} />
        }
        </>
    );
}

export default ProbePage;